import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function PricingSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnnual, setIsAnnual] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const plans = [
    {
      name: t('pricing.business.name'),
      description: t('pricing.business.desc'),
      monthlyPrice: 99,
      annualPrice: 89,
      monthlyLink: 'https://buy.stripe.com/7sYbJ05Y833U9dpcr208g00',
      annualLink: 'https://buy.stripe.com/3cIbJ0gCMawmexJ4YA08g03',
      features: [
        t('pricing.business.feature2'),
        t('pricing.business.feature3'),
        t('pricing.business.feature4'),
        t('pricing.business.feature5'),
        t('pricing.business.feature6'),
        t('pricing.business.feature7')
      ],
      icon: 'ri-vip-crown-line',
      gradient: 'from-[#10B981] to-[#1E3A8A]',
      popular: true,
      delay: '0s'
    },
    {
      name: t('pricing.enterprise.name'),
      description: t('pricing.enterprise.desc'),
      monthlyPrice: 199,
      annualPrice: 179,
      monthlyLink: 'https://buy.stripe.com/00w00i86g0VM0GTaiU08g01',
      annualLink: 'https://buy.stripe.com/dRm7sK2LW7kablx76I08g02',
      features: [
        t('pricing.enterprise.feature2'),
        t('pricing.enterprise.feature3'),
        t('pricing.enterprise.feature4'),
        t('pricing.enterprise.feature5'),
        t('pricing.enterprise.feature6'),
        t('pricing.enterprise.feature7'),
        t('pricing.enterprise.feature8'),
        t('pricing.enterprise.feature9')
      ],
      icon: 'ri-building-line',
      gradient: 'from-[#10B981] to-[#1E3A8A]',
      popular: false,
      delay: '0.2s'
    }
  ];

  const handlePlanClick = (plan: typeof plans[0]) => {
    const link = isAnnual ? plan.annualLink : plan.monthlyLink;
    window.location.href = link;
  };

  return (
    <section ref={sectionRef} id="preise" className="py-16 md:py-24 lg:py-32 bg-slate-50 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 right-0 w-48 h-48 md:w-96 md:h-96 bg-gradient-to-r from-[#10B981] to-[#1E3A8A] rounded-full blur-[100px] animate-pulse"></div>
      <div className="absolute bottom-0 left-0 w-48 h-48 md:w-96 md:h-96 bg-emerald-100 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '1s' }}></div>

      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-12 md:mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-white/50 backdrop-blur-sm rounded-full border border-blue-200 mb-4 md:mb-6 shadow-sm">
            <i className="ri-price-tag-3-line text-white text-sm md:text-base"></i>
            <span className="text-xs md:text-sm font-medium text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A]" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              {t('pricing.badge')}
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#0F172A] mb-4 md:mb-6 px-4" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
            {t('pricing.title')}{' '}
            <span className="bg-gradient-to-r from-tron-primary to-tron-primary bg-clip-text text-transparent animate-pulse">
              {t('pricing.titleHighlight')}
            </span>
          </h2>
          <p className="text-base md:text-lg lg:text-xl text-slate-600 max-w-3xl mx-auto mb-8 md:mb-12 px-4" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            {t('pricing.subtitle')}
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-3 md:gap-4 p-1.5 md:p-2 bg-white border border-slate-200 rounded-full shadow-sm backdrop-blur-sm">
            <button
              onClick={() => setIsAnnual(false)}
              className={`px-4 py-2 md:px-6 md:py-3 rounded-full font-semibold text-sm md:text-base transition-all duration-300 whitespace-nowrap ${!isAnnual
                ? 'bg-gradient-to-r from-[#10B981] to-[#1E3A8A] text-white shadow-md'
                : 'text-slate-500 hover:text-[#0F172A]'
                }`}
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              {t('pricing.monthly')}
            </button>
            <button
              onClick={() => setIsAnnual(true)}
              className={`px-4 py-2 md:px-6 md:py-3 rounded-full font-semibold text-sm md:text-base transition-all duration-300 whitespace-nowrap flex items-center gap-2 ${isAnnual
                ? 'bg-gradient-to-r from-[#10B981] to-[#1E3A8A] text-white shadow-md'
                : 'text-slate-500 hover:text-[#0F172A]'
                }`}
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              {t('pricing.annually')}
              <span className={`px-2 py-0.5 md:px-2.5 md:py-1 text-xs rounded-full ${isAnnual ? 'bg-white/20 text-white' : 'bg-emerald-100 text-tron-secondary'}`}>
                {t('pricing.discount')}
              </span>
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`relative bg-white rounded-2xl md:rounded-3xl p-6 md:p-8 border transition-all duration-500 ${plan.popular
                ? 'border-blue-200 shadow-xl scale-100 md:scale-105 bg-white'
                : 'border-slate-200 hover:border-emerald-300 hover:shadow-lg'
                } ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{
                transitionDelay: isVisible ? plan.delay : '0s',
                cursor: 'pointer'
              }}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute -top-3 md:-top-4 left-1/2 -translate-x-1/2 px-3 py-1 md:px-4 md:py-1.5 bg-gradient-to-r from-tron-primary to-tron-primary text-white text-xs md:text-sm font-bold rounded-full shadow-md whitespace-nowrap" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                  {t('pricing.popularBadge')}
                </div>
              )}

              {/* Icon */}
              <div className={`w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br ${plan.gradient} rounded-2xl flex items-center justify-center mb-5 md:mb-6 shadow-md border border-white/20`}>
                <i className={`${plan.icon} text-2xl md:text-3xl text-white`}></i>
              </div>

              {/* Plan Name */}
              <h3 className="text-xl md:text-2xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
                {plan.name}
              </h3>
              <p className="text-sm md:text-base text-slate-600 mb-5 md:mb-6" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                {plan.description}
              </p>

              {/* Price */}
              <div className="mb-6 md:mb-8">
                <div className="flex items-baseline gap-1 md:gap-2">
                  <span className="text-4xl sm:text-5xl md:text-6xl font-bold bg-gradient-to-r from-[#10B981] to-[#1E3A8A] bg-clip-text text-transparent" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
                    €{isAnnual ? plan.annualPrice : plan.monthlyPrice}
                  </span>
                  <span className="text-base md:text-lg text-slate-500" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                    {t('pricing.perMonth')}
                  </span>
                </div>
                {isAnnual && (
                  <p className="text-xs md:text-sm text-tron-secondary mt-2" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                    {t('pricing.billedAnnually')} (€{plan.annualPrice * 12}{t('pricing.year')})
                  </p>
                )}
              </div>

              {/* Features */}
              <div className="space-y-3 md:space-y-4 mb-6 md:mb-8">
                {plan.features.map((feature, idx) => {
                  const isEnterpriseAddon = plan.name === t('pricing.enterprise.name') && idx > 0;
                  return (
                    <div key={idx} className="flex items-start gap-2.5 md:gap-3">
                      <div className={`w-5 h-5 md:w-6 md:h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 border bg-gradient-to-br from-[#10B981] to-[#1E3A8A] shadow-sm`}>
                        <i className={`${isEnterpriseAddon ? 'ri-add-line text-white font-bold' : 'ri-check-line text-white'} text-xs md:text-sm`}></i>
                      </div>
                      <span className="text-sm md:text-base text-slate-600" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                        {feature}
                      </span>
                    </div>
                  )
                })}
              </div>

              {/* CTA Button */}
              <button
                onClick={() => handlePlanClick(plan)}
                className={`w-full py-3 md:py-4 rounded-full font-semibold text-base md:text-lg transition-all duration-300 hover:scale-105 whitespace-nowrap flex items-center justify-center gap-2 ${plan.popular
                  ? 'bg-gradient-to-r from-tron-primary to-tron-primary text-white shadow-md hover:shadow-lg'
                  : 'border border-blue-200 text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A] hover:bg-gradient-to-r from-[#10B981] to-[#1E3A8A] hover:border-blue-300'
                  }`}
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                {t('pricing.cta')}
              </button>
            </div>
          ))}
        </div>

        {/* Bottom Note */}
        <div className={`text-center mt-12 md:mt-16 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <p className="text-sm md:text-base text-slate-500 px-4" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            {t('pricing.note')} <strong className="text-slate-700">{t('pricing.noteBold')}</strong> {t('pricing.noteEnd')}
          </p>
        </div>
      </div>
    </section>
  );
}
